// Puerto de packages/hce/src/hce/paciente/SignosVitalesTab.tsx. Suma
// (Fase 3, Enfermería) el acceso al 020 ampliado, la columna de turno y el
// resumen de balance hídrico (sección D del 020) por turno y por 24 h —
// calculado con calcularBalanceHidrico (formulario/discretos/enfermeria.ts),
// nunca guardado.
import { useResource, useSearchResources } from '@medplum/react-hooks';
import type { Observation, Patient } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { codigosFormularioDeEspecialidad } from '@/lib/catalogoEspecialidades';
import { enlaceConEncounter, PARAM_ENCOUNTER } from '@/lib/enlaceEncounter';
import { useEspecialidadDelPracticante } from '@/lib/useEspecialidadDelPracticante';
import { calcularBalanceHidrico, registrosLiquidosDe, SISTEMA_ENFERMERIA_020, textoValorObservacion, turnoDeObservacion, TURNOS } from '../formulario/discretos/enfermeria';
import type { CurvaPercentilDatos } from './curvaPercentilPorEdad';
import {
  curvaImcEdad0a5De,
  curvaImcEdadDe,
  curvaLongitudFentonDe,
  curvaPerimetroCefalicoEdadDe,
  curvaPerimetroCefalicoFentonDe,
  curvaPesoEdad5a10De,
  curvaPesoEdadDe,
  curvaPesoFentonDe,
  curvaPesoLongitudDe,
  curvaPesoTallaDe,
  curvaTallaEdad5a19De,
  curvaTallaEdadDe,
  formatoEdadGestacionalDesdeDias,
} from './curvaPercentilPorEdad';
import { hayDatosSuficientes, seriesCurvaTermicaDe } from './curvaTermica';
import { TablaVacia } from './TablaVacia';

// Lazy: el chunk de recharts (vía CurvaTermica020/CurvaPercentil) solo se
// pide cuando hace falta — curvaTermica.ts/curvaPercentilPorEdad.ts (las
// funciones que deciden eso) no importan recharts, así que una ficha sin
// datos vitales nunca paga ese costo. Ver PLAN.md, Fase 1 y Serie 028.
const CurvaTermica020 = lazy(() => import('./CurvaTermica020').then((m) => ({ default: m.CurvaTermica020 })));
const CurvaPercentil = lazy(() => import('./CurvaPercentil').then((m) => ({ default: m.CurvaPercentil })));

/** Eje X en cm (peso para la longitud/talla): el eje no es la edad sino la longitud/talla. */
const formatoCm = (cm: number): string => `${Number.isInteger(cm) ? cm : cm.toFixed(1)} cm`;

/** Una sección de curva de crecimiento (peso/talla/perímetro cefálico) — mismo patrón lazy+Suspense repetido varias veces, extraído para no triplicar el boilerplate. */
function SeccionCurvaPercentil({ titulo, datos, mensajeInsuficiente, etiquetaEjeY, ariaLabel, formatoEjeX }: {
  titulo: string;
  datos: CurvaPercentilDatos | undefined;
  mensajeInsuficiente: string;
  etiquetaEjeY: string;
  ariaLabel: string;
  /** `undefined` = eje en meses/años (la mayoría) — 028A-1 (Fenton) pasa `formatoEdadGestacionalDesdeDias` (semanas), eje distinto porque el dato es edad postmenstrual. */
  formatoEjeX?: (dias: number) => string;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <section className="flex flex-col gap-2">
      <h5 className="text-sm font-semibold">{titulo}</h5>
      {datos ? (
        <Suspense fallback={<p className="text-sm text-muted-foreground">{t('fichaPaciente.cargando')}</p>}>
          <CurvaPercentil datos={datos} etiquetaEjeY={etiquetaEjeY} ariaLabel={ariaLabel} formatoEjeX={formatoEjeX} />
        </Suspense>
      ) : (
        <p className="text-sm text-muted-foreground">{mensajeInsuficiente}</p>
      )}
    </section>
  );
}

/** Días de balance que se muestran (los más recientes). */
const DIAS_BALANCE = 3;

function BalanceHidrico({ patientId }: { patientId: string }): JSX.Element {
  const { t } = useTranslation();
  const [observaciones, cargando] = useSearchResources('Observation', {
    patient: `Patient/${patientId}`,
    category: `${SISTEMA_ENFERMERIA_020}|ingesta-eliminacion`,
    _sort: '-date',
    _count: 500,
  });
  if (cargando) return <p className="text-sm text-muted-foreground">{t('fichaPaciente.cargando')}</p>;
  const dias = calcularBalanceHidrico(registrosLiquidosDe((observaciones ?? []) as Observation[])).slice(0, DIAS_BALANCE);
  if (dias.length === 0) return <p className="text-sm text-muted-foreground">{t('fichaPaciente.balance.sinDatos')}</p>;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('enfermeria.diaEnfermeria')}</TableHead>
          <TableHead>{t('enfermeria.turno')}</TableHead>
          <TableHead className="text-right">{t('fichaPaciente.balance.ingresos')}</TableHead>
          <TableHead className="text-right">{t('fichaPaciente.balance.eliminaciones')}</TableHead>
          <TableHead className="text-right">{t('fichaPaciente.balance.balance')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {dias.flatMap((d) => [
          ...TURNOS.map((turno) => (
            <TableRow key={`${d.dia}-${turno}`}>
              <TableCell>{d.dia}</TableCell>
              <TableCell>{turno}</TableCell>
              <TableCell className="text-right font-mono">{d.porTurno[turno].ingresos}</TableCell>
              <TableCell className="text-right font-mono">{d.porTurno[turno].eliminaciones}</TableCell>
              <TableCell className="text-right font-mono">{d.porTurno[turno].balance}</TableCell>
            </TableRow>
          )),
          <TableRow key={`${d.dia}-24h`} className="font-semibold">
            <TableCell>{d.dia}</TableCell>
            <TableCell>{t('fichaPaciente.balance.total24h')}</TableCell>
            <TableCell className="text-right font-mono">{d.total24h.ingresos}</TableCell>
            <TableCell className="text-right font-mono">{d.total24h.eliminaciones}</TableCell>
            <TableCell className="text-right font-mono">{d.total24h.balance}</TableCell>
          </TableRow>,
        ])}
      </TableBody>
    </Table>
  );
}

export function SignosVitalesTab(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams() as { id: string };
  // El Encounter abierto que PatientChartPage le cuelga a esta pestaña (ver
  // lib/enlaceEncounter.ts): se reenvía al 020 para que el Observation que se
  // registre quede vinculado a la consulta/estancia, no suelto.
  const [searchParams] = useSearchParams();
  const encounterRef = searchParams.get(PARAM_ENCOUNTER);
  const especialidad = useEspecialidadDelPracticante();
  const paciente = useResource<Patient>({ reference: `Patient/${id}` });
  const [observaciones, cargando] = useSearchResources('Observation', { patient: `Patient/${id}`, category: 'vital-signs', _sort: '-date', _count: 200 });
  const puedeRegistrar020 = !especialidad.cargando && !!especialidad.codigo && codigosFormularioDeEspecialidad(especialidad.codigo).includes('020');
  const lista = (observaciones ?? []) as Observation[];
  const curvaPesoEdad = paciente ? curvaPesoEdadDe(lista, paciente) : undefined;
  const curvaTallaEdad = paciente ? curvaTallaEdadDe(lista, paciente) : undefined;
  const curvaPerimetroCefalicoEdad = paciente ? curvaPerimetroCefalicoEdadDe(lista, paciente) : undefined;
  const curvaTallaEdad5a19 = paciente ? curvaTallaEdad5a19De(lista, paciente) : undefined;
  const curvaPesoEdad5a10 = paciente ? curvaPesoEdad5a10De(lista, paciente) : undefined;
  const curvaImcEdad = paciente ? curvaImcEdadDe(lista, paciente) : undefined;
  const curvaImcEdad0a5 = paciente ? curvaImcEdad0a5De(lista, paciente) : undefined;
  const curvaPesoLongitud = paciente ? curvaPesoLongitudDe(lista, paciente) : undefined;
  const curvaPesoTalla = paciente ? curvaPesoTallaDe(lista, paciente) : undefined;
  const curvaPesoFenton = paciente ? curvaPesoFentonDe(lista, paciente) : undefined;
  const curvaLongitudFenton = paciente ? curvaLongitudFentonDe(lista, paciente) : undefined;
  const curvaPerimetroCefalicoFenton = paciente ? curvaPerimetroCefalicoFentonDe(lista, paciente) : undefined;

  return (
    <div className="flex flex-col gap-6">
      {puedeRegistrar020 && (
        <Button asChild size="sm" variant="outline" className="w-fit">
          <Link to={enlaceConEncounter(`/${id}/formulario/020`, encounterRef)}>{t('fichaPaciente.balance.registrar020')}</Link>
        </Button>
      )}

      <section className="flex flex-col gap-2">
        <h5 className="text-sm font-semibold">{t('fichaPaciente.balance.titulo')}</h5>
        <BalanceHidrico patientId={id} />
      </section>

      <section className="flex flex-col gap-2">
        <h5 className="text-sm font-semibold">{t('fichaPaciente.curvaTermica.titulo')}</h5>
        {!cargando &&
          (hayDatosSuficientes(seriesCurvaTermicaDe(lista)) ? (
            <Suspense fallback={<p className="text-sm text-muted-foreground">{t('fichaPaciente.cargando')}</p>}>
              <CurvaTermica020 observaciones={lista} />
            </Suspense>
          ) : (
            <p className="text-sm text-muted-foreground">{t('fichaPaciente.curvaTermica.datosInsuficientes')}</p>
          ))}
      </section>

      {!cargando && (
        <>
          <SeccionCurvaPercentil
            titulo={t('fichaPaciente.curvaPesoFenton.titulo')}
            datos={curvaPesoFenton}
            mensajeInsuficiente={t('fichaPaciente.curvaPesoFenton.datosInsuficientes')}
            etiquetaEjeY="Peso (kg)"
            ariaLabel="Curva de crecimiento peso/edad postmenstrual (Fenton 2003, prematuros) — líneas de referencia (-3 a +3 desviaciones estándar) y peso real del paciente"
            formatoEjeX={formatoEdadGestacionalDesdeDias}
          />
          <SeccionCurvaPercentil
            titulo={t('fichaPaciente.curvaLongitudFenton.titulo')}
            datos={curvaLongitudFenton}
            mensajeInsuficiente={t('fichaPaciente.curvaLongitudFenton.datosInsuficientes')}
            etiquetaEjeY="Longitud (cm)"
            ariaLabel="Curva de crecimiento longitud/edad postmenstrual (Fenton 2003, prematuros) — líneas de referencia (-3 a +3 desviaciones estándar) y longitud real del paciente"
            formatoEjeX={formatoEdadGestacionalDesdeDias}
          />
          <SeccionCurvaPercentil
            titulo={t('fichaPaciente.curvaPerimetroCefalicoFenton.titulo')}
            datos={curvaPerimetroCefalicoFenton}
            mensajeInsuficiente={t('fichaPaciente.curvaPerimetroCefalicoFenton.datosInsuficientes')}
            etiquetaEjeY="Perímetro cefálico (cm)"
            ariaLabel="Curva de crecimiento perímetro cefálico/edad postmenstrual (Fenton 2003, prematuros) — líneas de referencia (-3 a +3 desviaciones estándar) y perímetro cefálico real del paciente"
            formatoEjeX={formatoEdadGestacionalDesdeDias}
          />
          <SeccionCurvaPercentil
            titulo={t('fichaPaciente.curvaPesoEdad.titulo')}
            datos={curvaPesoEdad}
            mensajeInsuficiente={t('fichaPaciente.curvaPesoEdad.datosInsuficientes')}
            etiquetaEjeY="Peso (kg)"
            ariaLabel="Curva de crecimiento peso/edad — líneas de referencia OMS (-3 a +3 desviaciones estándar) y peso real del paciente"
          />
          <SeccionCurvaPercentil
            titulo={t('fichaPaciente.curvaTallaEdad.titulo')}
            datos={curvaTallaEdad}
            mensajeInsuficiente={t('fichaPaciente.curvaTallaEdad.datosInsuficientes')}
            etiquetaEjeY="Talla (cm)"
            ariaLabel="Curva de crecimiento talla/edad — líneas de referencia OMS (-3 a +3 desviaciones estándar) y talla real del paciente"
          />
          <SeccionCurvaPercentil
            titulo={t('fichaPaciente.curvaPerimetroCefalicoEdad.titulo')}
            datos={curvaPerimetroCefalicoEdad}
            mensajeInsuficiente={t('fichaPaciente.curvaPerimetroCefalicoEdad.datosInsuficientes')}
            etiquetaEjeY="Perímetro cefálico (cm)"
            ariaLabel="Curva de crecimiento perímetro cefálico/edad — líneas de referencia OMS (-3 a +3 desviaciones estándar) y perímetro cefálico real del paciente"
          />
          <SeccionCurvaPercentil
            titulo={t('fichaPaciente.curvaPesoLongitud.titulo')}
            datos={curvaPesoLongitud}
            mensajeInsuficiente={t('fichaPaciente.curvaPesoLongitud.datosInsuficientes')}
            etiquetaEjeY="Peso (kg)"
            ariaLabel="Curva de peso para la longitud (0-2 años) — líneas de referencia OMS (-3 a +3 desviaciones estándar) y peso real del paciente contra su longitud"
            formatoEjeX={formatoCm}
          />
          <SeccionCurvaPercentil
            titulo={t('fichaPaciente.curvaPesoTalla.titulo')}
            datos={curvaPesoTalla}
            mensajeInsuficiente={t('fichaPaciente.curvaPesoTalla.datosInsuficientes')}
            etiquetaEjeY="Peso (kg)"
            ariaLabel="Curva de peso para la talla (2-5 años) — líneas de referencia OMS (-3 a +3 desviaciones estándar) y peso real del paciente contra su talla"
            formatoEjeX={formatoCm}
          />
          <SeccionCurvaPercentil
            titulo={t('fichaPaciente.curvaImcEdad0a5.titulo')}
            datos={curvaImcEdad0a5}
            mensajeInsuficiente={t('fichaPaciente.curvaImcEdad0a5.datosInsuficientes')}
            etiquetaEjeY="IMC (kg/m²)"
            ariaLabel="Curva de IMC/edad (0-5 años) — líneas de referencia OMS (-3 a +3 desviaciones estándar) e IMC calculado del paciente"
          />
          <SeccionCurvaPercentil
            titulo={t('fichaPaciente.curvaTallaEdad5a19.titulo')}
            datos={curvaTallaEdad5a19}
            mensajeInsuficiente={t('fichaPaciente.curvaTallaEdad5a19.datosInsuficientes')}
            etiquetaEjeY="Talla (cm)"
            ariaLabel="Curva de crecimiento talla/edad (5-19 años) — líneas de referencia OMS (-3 a +3 desviaciones estándar) y talla real del paciente"
          />
          <SeccionCurvaPercentil
            titulo={t('fichaPaciente.curvaPesoEdad5a10.titulo')}
            datos={curvaPesoEdad5a10}
            mensajeInsuficiente={t('fichaPaciente.curvaPesoEdad5a10.datosInsuficientes')}
            etiquetaEjeY="Peso (kg)"
            ariaLabel="Curva de crecimiento peso/edad (5-10 años, tope oficial de la OMS) — líneas de referencia OMS (-3 a +3 desviaciones estándar) y peso real del paciente"
          />
          <SeccionCurvaPercentil
            titulo={t('fichaPaciente.curvaImcEdad.titulo')}
            datos={curvaImcEdad}
            mensajeInsuficiente={t('fichaPaciente.curvaImcEdad.datosInsuficientes')}
            etiquetaEjeY="IMC (kg/m²)"
            ariaLabel="Curva de IMC/edad (5-19 años) — líneas de referencia OMS (-3 a +3 desviaciones estándar) e IMC calculado del paciente"
          />
        </>
      )}

      <section className="flex flex-col gap-2">
        <h5 className="text-sm font-semibold">{t('fichaPaciente.tabs.signosVitales')}</h5>
        {cargando ? (
          <p className="text-sm text-muted-foreground">{t('fichaPaciente.cargando')}</p>
        ) : lista.length === 0 ? (
          <TablaVacia mensaje={t('fichaPaciente.sinDatos')} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('fichaPaciente.columnas.fecha')}</TableHead>
                <TableHead>{t('enfermeria.turno')}</TableHead>
                <TableHead>{t('fichaPaciente.columnas.signo')}</TableHead>
                <TableHead>{t('fichaPaciente.columnas.valor')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lista.map((o) => (
                <TableRow key={o.id}>
                  <TableCell>{o.effectiveDateTime ? new Date(o.effectiveDateTime).toLocaleString('es-EC') : '—'}</TableCell>
                  <TableCell>{turnoDeObservacion(o) ?? '—'}</TableCell>
                  <TableCell>{o.code?.text ?? o.code?.coding?.[0]?.display ?? '—'}</TableCell>
                  <TableCell>{textoValorObservacion(o)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </section>
    </div>
  );
}
